import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Callout } from "../../../ui";
import useContacts from "../../../../hooks/useContacts";
import { Contact } from "../../../common/ContactForm";
import { useCountries, useStatesByCountry } from "../../../../hooks/useLocations";
import { Country, State } from "../../../../services/locationService";
import { useShipping } from "../../../../hooks/useShipping";
import { mapDocumentsToParcels } from "../../utils/documentCommodityUtils";

import { AddressSelectionProps, AddressType, AddressFormData } from "./types";
import { createContactFromForm } from "./utils";
import { useAddressValidation, useFormHandlers, PostalCodeErrors } from "./hooks";
import AddressFormSection from "./AddressFormSection";
import AddressBookDrawer from "./AddressBookDrawer";

const AddressSelection: React.FC<AddressSelectionProps> = ({
  visible,
  selectedSourceAddressId,
  selectedDestinationAddressId,
  isRTL,
  onSourceAddressSelect,
  onDestinationAddressSelect,
  onContinue,
  onBack,
  allowedOriginCountry,
  allowedDestinationCountry,
  shippingData,
  onRateValidation,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<AddressType>("source");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<AddressType>("source");
  const [hasAutoSelectedDefault, setHasAutoSelectedDefault] = useState(false);
  const [isValidatingRates, setIsValidatingRates] = useState(false);
  const [rateValidationError, setRateValidationError] = useState<string>("");

  const [postalCodeErrors, setPostalCodeErrors] = useState<PostalCodeErrors>({
    source: null,
    destination: null,
  });

  const handleSourcePostalCodeError = useCallback((error: string | null) => {
    setPostalCodeErrors((prev) => ({ ...prev, source: error }));
  }, []);

  const handleDestinationPostalCodeError = useCallback((error: string | null) => {
    setPostalCodeErrors((prev) => ({ ...prev, destination: error }));
  }, []);

  const {
    sourceForm,
    destinationForm,
    setSourceForm,
    setDestinationForm,
    handleSourceFormChange,
    handleDestinationFormChange,
    handleSourceGooglePlaceSelect,
    handleDestinationGooglePlaceSelect,
  } = useFormHandlers();

  const { getShippingRates } = useShipping();

  const {
    contacts = [],
    isLoading: isLoadingContacts,
    error: contactsError,
  } = useContacts({ searchQuery, defaultOnly: false, page: 1, per_page: 500 });

  const { data: countriesResponse, isLoading: isLoadingCountriesForValidation } = useCountries({ per_page: 500 });
  const allCountries = useMemo(() => (countriesResponse?.data || []) as Country[], [countriesResponse?.data]);

  const allowedOriginCountryId = useMemo(() => {
    if (!allowedOriginCountry || !allCountries.length || isLoadingCountriesForValidation) return null;
    return allCountries.find(
      (c) =>
        c.name?.toLowerCase() === allowedOriginCountry?.toLowerCase() ||
        c.iso2?.toLowerCase() === allowedOriginCountry?.toLowerCase() ||
        c.iso3?.toLowerCase() === allowedOriginCountry?.toLowerCase()
    )?.id || null;
  }, [allowedOriginCountry, allCountries, isLoadingCountriesForValidation]);

  const allowedDestinationCountryId = useMemo(() => {
    if (!allowedDestinationCountry || !allCountries.length || isLoadingCountriesForValidation) return null;
    return allCountries.find(
      (c) =>
        c.name?.toLowerCase() === allowedDestinationCountry?.toLowerCase() ||
        c.iso2?.toLowerCase() === allowedDestinationCountry?.toLowerCase() ||
        c.iso3?.toLowerCase() === allowedDestinationCountry?.toLowerCase()
    )?.id || null;
  }, [allowedDestinationCountry, allCountries, isLoadingCountriesForValidation]);

  const { data: sourceStatesResponse } = useStatesByCountry(sourceForm.country_id || undefined, { per_page: 500 });
  const sourceStates = useMemo(() => (sourceStatesResponse?.data || []) as State[], [sourceStatesResponse?.data]);

  const { data: destinationStatesResponse } = useStatesByCountry(destinationForm.country_id || undefined, { per_page: 500 });
  const destinationStates = useMemo(() => (destinationStatesResponse?.data || []) as State[], [destinationStatesResponse?.data]);

  const { isFormValid, isContactValidForSource, isContactValidForDestination } =
    useAddressValidation(allowedOriginCountryId, allowedDestinationCountryId, allCountries, postalCodeErrors);

  const canAutoSelectDefaultSource = useMemo(() => {
    if (!allowedOriginCountry) return true;
    if (isLoadingCountriesForValidation) return false;
    return Boolean(allowedOriginCountryId);
  }, [allowedOriginCountry, isLoadingCountriesForValidation, allowedOriginCountryId]);

  const defaultContact = useMemo(() => {
    if (!canAutoSelectDefaultSource) return null;
    return contacts.find((c) => c.isDefault && isContactValidForSource(c));
  }, [contacts, isContactValidForSource, canAutoSelectDefaultSource]);

  const selectedSourceContact = useMemo<Contact | null>(
    () => (!selectedSourceAddressId ? null : contacts.find((c) => c.id === selectedSourceAddressId) || null),
    [contacts, selectedSourceAddressId]
  );
  const selectedDestinationContact = useMemo<Contact | null>(
    () => (!selectedDestinationAddressId ? null : contacts.find((c) => c.id === selectedDestinationAddressId) || null),
    [contacts, selectedDestinationAddressId]
  );

  // Auto-select default contact
  useEffect(() => {
    if (defaultContact && !selectedSourceAddressId && !hasAutoSelectedDefault && contacts.length > 0) {
      const contactForm: AddressFormData = {
        nickname: defaultContact.nickname || "",
        contactName: defaultContact.contactName || "",
        companyName: defaultContact.companyName || "",
        email: defaultContact.email || "",
        phone: defaultContact.phone || "",
        nationalId: defaultContact.nationalId || "",
        street: defaultContact.street || "",
        houseNo: defaultContact.houseNo || "",
        zipCode: defaultContact.zipCode || "",
        deliveryDetails: defaultContact.deliveryDetails || "",
        country_id: defaultContact.country_id || null,
        state_id: defaultContact.state_id || null,
        state_other:
          typeof defaultContact.state === "object" && defaultContact.state?.name
            ? defaultContact.state.name
            : typeof defaultContact.state === "string" ? defaultContact.state : "",
        city_id: defaultContact.city_id || null,
        city_other:
          defaultContact.city_other ||
          (typeof defaultContact.city === "object" && defaultContact.city?.name
            ? defaultContact.city.name
            : typeof defaultContact.city === "string" ? defaultContact.city : ""),
      };
      onSourceAddressSelect(defaultContact);
      setSourceForm(contactForm);
      setHasAutoSelectedDefault(true);
      setTimeout(() => setActiveTab("destination"), 500);
    }
  }, [defaultContact, selectedSourceAddressId, hasAutoSelectedDefault, contacts.length, onSourceAddressSelect, setSourceForm]);

  // Rate validation
  const validateRatesWithAddresses = useCallback(async (sourceAddress: Contact, destinationAddress: Contact) => {
    if (!shippingData) return true;
    setIsValidatingRates(true);
    setRateValidationError("");
    try {
      const sourceCountry = allCountries.find((c) => c.id === sourceAddress.country_id);
      const destinationCountry = allCountries.find((c) => c.id === destinationAddress.country_id);
      if (!sourceCountry || !destinationCountry) throw new Error("Could not determine country information");

      // Documents are tracked separately in the UI; the rate API only accepts a
      // `parcels` array, so convert documents to parcels for document shipments.
      const requestParcels =
        shippingData.shippingType === "document"
          ? mapDocumentsToParcels(shippingData.documents)
          : shippingData.parcels || [];

      const rateRequestData = {
        origin: sourceCountry.iso2,
        destination: destinationCountry.iso2,
        originPostalCode: sourceAddress.zipCode || shippingData.originPostalCode,
        destinationPostalCode: destinationAddress.zipCode || shippingData.destinationPostalCode,
        shippingType: shippingData.shippingType,
        shipmentType: shippingData.shipmentType,
        parcels: requestParcels,
        totalWeight:
          requestParcels.reduce(
            (total: number, parcel: any) => total + (parcel.physicalWeight || parcel.weight || 0),
            0
          ) || 0.5,
        unitSystem: shippingData.unitSystem,
        isInternational: shippingData.shipmentType === "international",
        compareAllProviders: true,
      };
      const ratesResponse = await getShippingRates(rateRequestData);
      if (ratesResponse?.success === false) throw new Error(ratesResponse.error_message || "Failed to validate addresses.");
      const rates = Array.isArray(ratesResponse) ? ratesResponse : [];
      if (onRateValidation) onRateValidation(true, rates);
      return true;
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || error?.message || "Failed to validate addresses. Please check your addresses and try again.";
      setRateValidationError(msg);
      if (onRateValidation) onRateValidation(false);
      return false;
    } finally {
      setIsValidatingRates(false);
    }
  }, [shippingData, allCountries, getShippingRates, onRateValidation]);

  const sourceFormValid = useMemo(() => isFormValid(sourceForm, false), [sourceForm, isFormValid]);
  const destinationFormValid = useMemo(() => isFormValid(destinationForm, true), [destinationForm, isFormValid]);

  useEffect(() => { if (destinationFormValid) setActiveTab("destination"); }, [destinationFormValid]);

  useEffect(() => {
    if (rateValidationError) setRateValidationError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceForm.country_id, sourceForm.zipCode, sourceForm.street, destinationForm.country_id, destinationForm.zipCode, destinationForm.street, selectedSourceAddressId, selectedDestinationAddressId]);

  const canContinue = useMemo(
    () => sourceFormValid && destinationFormValid && !rateValidationError,
    [sourceFormValid, destinationFormValid, rateValidationError]
  );

  const handleTabChange = useCallback((newTab: AddressType) => {
    if (newTab === "source") { setActiveTab(newTab); return; }
    if (newTab === "destination" && !sourceFormValid && !selectedSourceAddressId) return;
    setActiveTab(newTab);
  }, [sourceFormValid, selectedSourceAddressId]);

  const handleAddressBookOpen = useCallback((type: AddressType) => {
    setDrawerType(type);
    setDrawerOpen(true);
    setSearchQuery("");
  }, []);

  const handleAddressSelect = useCallback((contact: Contact) => {
    const isValid = drawerType === "source" ? isContactValidForSource(contact) : isContactValidForDestination(contact);
    if (!isValid) return;
    const contactForm: AddressFormData = {
      nickname: contact.nickname || "",
      contactName: contact.contactName || "",
      companyName: contact.companyName || "",
      email: contact.email || "",
      phone: contact.phone || "",
      nationalId: contact.nationalId || "",
      street: contact.street || "",
      houseNo: contact.houseNo || "",
      zipCode: contact.zipCode || "",
      deliveryDetails: contact.deliveryDetails || "",
      country_id: contact.country_id || null,
      state_id: contact.state_id || null,
      state_other:
        typeof contact.state === "object" && contact.state?.name
          ? contact.state.name
          : typeof contact.state === "string" ? contact.state : "",
      city_id: contact.city_id || null,
      city_other:
        contact.city_other ||
        (typeof contact.city === "object" && contact.city?.name
          ? contact.city.name
          : typeof contact.city === "string" ? contact.city : ""),
    };
    if (drawerType === "source") { onSourceAddressSelect(contact); setSourceForm(contactForm); }
    else { onDestinationAddressSelect(contact); setDestinationForm(contactForm); }
    setDrawerOpen(false);
  }, [drawerType, isContactValidForSource, isContactValidForDestination, onSourceAddressSelect, onDestinationAddressSelect, setSourceForm, setDestinationForm]);

  if (!visible) return null;

  const sourceReady = sourceFormValid || Boolean(selectedSourceAddressId);
  const destReady = destinationFormValid || Boolean(selectedDestinationAddressId);

  return (
    <div style={{ transition: "all 0.3s ease" }}>
      <div className="ship-sheet">
        {/* Restrictions callout (only when present) */}
        {(allowedOriginCountry || allowedDestinationCountry) && (
          <Callout tone="info">
            <span>
              <strong>Shipping restrictions:</strong> Collection from{' '}
              {allowedOriginCountry || 'any'} → Delivery to {allowedDestinationCountry || 'any'}
            </span>
          </Callout>
        )}

        {/* 01. Ship From */}
        <section className="ship-row" id="address-source">
          <header className="ship-row-head">
            <span className="ship-num">01</span>
            <div>
              <h3>Ship from</h3>
              <p>Where the carrier will collect the parcel.</p>
              {sourceReady && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 8,
                    padding: '3px 8px',
                    borderRadius: 99,
                    background: 'var(--ok-bg)',
                    color: 'var(--ok)',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  ✓ {hasAutoSelectedDefault ? 'Default selected' : 'Complete'}
                </span>
              )}
            </div>
          </header>
          <div className="ship-row-body">
            <AddressFormSection
              title=""
              type="source"
              form={sourceForm}
              icon={null as any}
              color="#2B55FF"
              onFormChange={handleSourceFormChange}
              onAddressBookOpen={() => handleAddressBookOpen('source')}
              onGooglePlaceSelect={handleSourceGooglePlaceSelect}
              isFormValid={sourceReady}
              allowedOriginCountryId={allowedOriginCountryId}
              allowedDestinationCountryId={allowedDestinationCountryId}
              allCountries={allCountries}
              onNextStep={() => {
                setActiveTab('destination');
                document
                  .getElementById('address-destination')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              onPostalCodeError={handleSourcePostalCodeError}
            />
            {hasAutoSelectedDefault && selectedSourceAddressId && (
              <div style={{ marginTop: 10 }}>
                <Callout tone="info">
                  <span>Your default address has been pre-filled — edit any field to override.</span>
                </Callout>
              </div>
            )}
          </div>
        </section>

        {/* 02. Deliver To */}
        <section className="ship-row" id="address-destination">
          <header className="ship-row-head">
            <span className="ship-num">02</span>
            <div>
              <h3>Deliver to</h3>
              <p>Where the parcel should arrive.</p>
              {destReady && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 8,
                    padding: '3px 8px',
                    borderRadius: 99,
                    background: 'var(--ok-bg)',
                    color: 'var(--ok)',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  ✓ Complete
                </span>
              )}
              {!sourceReady && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 8,
                    padding: '3px 8px',
                    borderRadius: 99,
                    background: 'var(--warn-bg)',
                    color: 'var(--warn)',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  Complete ship-from first
                </span>
              )}
            </div>
          </header>
          <div className="ship-row-body" style={{ opacity: sourceReady ? 1 : 0.55, pointerEvents: sourceReady ? 'auto' : 'none' }}>
            <AddressFormSection
              title=""
              type="destination"
              form={destinationForm}
              icon={null as any}
              color="#6366f1"
              onFormChange={handleDestinationFormChange}
              onAddressBookOpen={() => handleAddressBookOpen('destination')}
              onGooglePlaceSelect={handleDestinationGooglePlaceSelect}
              isFormValid={destReady}
              allowedOriginCountryId={allowedOriginCountryId}
              allowedDestinationCountryId={allowedDestinationCountryId}
              allCountries={allCountries}
              onPostalCodeError={handleDestinationPostalCodeError}
            />
          </div>
        </section>

        {/* Hidden tab bar shell preserved (no UI; activeTab is referenced by setActiveTab elsewhere) */}
        <div style={{ display: 'none' }}>
          {(['source', 'destination'] as AddressType[]).map((tab) => {
            const active = activeTab === tab;
            const isSource = tab === "source";
            const ready = isSource ? sourceReady : destReady;
            const disabled = !isSource && !sourceReady;
            const accentColor = isSource ? "var(--brand-blue)" : "var(--brand-ink)";

            return (
              <button
                key={tab}
                type="button"
                disabled={disabled}
                onClick={() => handleTabChange(tab)}
                aria-hidden
                style={{ display: active && ready && accentColor ? 'none' : 'none' }}
              />
            );
          })}
        </div>
        <div style={{ display: 'none' }}>
          {(["source", "destination"] as AddressType[]).map((tab) => {
            const active = activeTab === tab;
            const isSource = tab === "source";
            const ready = isSource ? sourceReady : destReady;
            const disabled = !isSource && !sourceReady;
            const accentColor = isSource ? "var(--brand-blue)" : "var(--brand-ink)";

            return (
              <button
                key={tab}
                type="button"
                disabled={disabled}
                onClick={() => handleTabChange(tab)}
                style={{
                  flex: 1,
                  padding: "14px 16px",
                  border: "none",
                  borderBottom: active ? `3px solid ${accentColor}` : "3px solid transparent",
                  background: active ? `rgba(43,85,255,0.05)` : "transparent",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.5 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  transition: "all 0.2s ease",
                  borderRadius: "8px 8px 0 0",
                }}
                onMouseEnter={(e) => {
                  if (!disabled && !active)
                    (e.currentTarget as HTMLButtonElement).style.background = "rgba(43,85,255,0.03)";
                }}
                onMouseLeave={(e) => {
                  if (!active)
                    (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: active
                      ? `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`
                      : `rgba(43,85,255,0.08)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: active ? "#fff" : accentColor,
                    fontSize: 18,
                    flexShrink: 0,
                    transition: "all 0.3s ease",
                  }}
                >
                  📍
                </div>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: active ? accentColor : "var(--ink-2)" }}>
                    {isSource ? "Ship From" : "Deliver To"}
                  </div>
                  {ready ? (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--ok)",
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        marginTop: 2,
                      }}
                    >
                      ✓ {isSource && hasAutoSelectedDefault ? "Default" : "Complete"}
                    </span>
                  ) : !isSource && !sourceReady ? (
                    <span style={{ fontSize: 11, color: "#b45309", marginTop: 2, display: "block" }}>
                      Complete first
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>

        {/* Continue section */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: 32,
            gap: 16,
          }}
        >
          {!canContinue && (sourceFormValid || destinationFormValid) && !rateValidationError && (
            <div style={{ maxWidth: 600, width: "100%" }}>
              <Callout tone="warn">Please select or enter valid addresses.</Callout>
            </div>
          )}

          {rateValidationError && (
            <div style={{ maxWidth: 600, width: "100%" }}>
              <Callout tone="warn">
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Rate Validation Failed</div>
                  <div>{rateValidationError}</div>
                </div>
              </Callout>
            </div>
          )}

          <Button
            variant="blue"
            disabled={!canContinue || isValidatingRates}
            onClick={async () => {
              let sourceContact: Contact | null = null;
              let destinationContact: Contact | null = null;

              if (sourceFormValid) {
                sourceContact = createContactFromForm(sourceForm, allCountries, sourceStates, selectedSourceContact);
                onSourceAddressSelect(sourceContact);
              }
              if (destinationFormValid) {
                destinationContact = createContactFromForm(destinationForm, allCountries, destinationStates, selectedDestinationContact);
                onDestinationAddressSelect(destinationContact);
              }
              if (!sourceContact || !destinationContact) return;

              if (shippingData) {
                const isValid = await validateRatesWithAddresses(sourceContact, destinationContact);
                if (!isValid) return;
              }

              onContinue(sourceContact, destinationContact);
            }}
            style={{
              padding: "14px 48px",
              fontSize: 16,
              fontWeight: 700,
              minWidth: 260,
              background: canContinue
                ? "linear-gradient(135deg, var(--brand-blue), var(--brand-ink))"
                : undefined,
              boxShadow: canContinue ? "0 8px 24px rgba(30,58,138,0.25)" : undefined,
            }}
          >
            {isValidatingRates ? "Validating Addresses…" : "Continue to Payment →"}
          </Button>
        </div>
      </div>

      {/* Address Book Drawer */}
      <AddressBookDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        drawerType={drawerType}
        contacts={contacts}
        isLoading={isLoadingContacts}
        error={contactsError}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onAddressSelect={handleAddressSelect}
        isContactValidForSource={isContactValidForSource}
        isContactValidForDestination={isContactValidForDestination}
        allowedOriginCountry={allowedOriginCountry}
        allowedDestinationCountry={allowedDestinationCountry}
      />
    </div>
  );
};

export default AddressSelection;
